import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';

import { OnboardingPhase, OnboardingTask } from '../../interfaces/onboarding.interfaces';
import { OnboardingService } from '../../services/onboarding.service';
import { OnboardingAnalyticsService } from '../../services/onboarding-analytics.service';
import { TourOrchestratorService } from '../../services/tour-orchestrator.service';

type OnboardingTab = 'mandatory' | 'optional';

@Component({
    selector: 'fina-phase-detail-modal',
    templateUrl: './phase-detail-modal.component.html',
    styleUrls: ['./phase-detail-modal.component.scss'],
})
export class PhaseDetailModalComponent implements OnInit, OnDestroy {
    phase: OnboardingPhase | null = null;
    activeTab: OnboardingTab = 'mandatory';
    isLoading = true;
    private subscription?: Subscription;
    /** La pestaña inicial se calcula UNA sola vez (primer estado real); luego los eventos de socket no la mueven. */
    private hasInitializedTab = false;

    constructor(
        public activeModal: NgbActiveModal,
        private onboardingService: OnboardingService,
        private router: Router,
        private tourOrchestrator: TourOrchestratorService,
        private analytics: OnboardingAnalyticsService,
    ) {}

    ngOnInit(): void {
        this.subscription = this.onboardingService.state$.subscribe(onboarding => {
            this.phase = this.onboardingService.getActivePhase(onboarding);

            // Pestaña inicial dinámica (una sola vez, sobre el primer estado REAL): si las obligatorias ya están
            // listas pero quedan opcionales aplicables pendientes, abrir directo en "Opcionales" para no mostrar
            // una lista de obligatorias terminadas. `&& this.phase` evita inicializar con la primera emisión nula
            // del BehaviorSubject `state$` (arranca en null hasta que resuelve el HTTP → sin tareas en qué basarse).
            if (!this.hasInitializedTab && this.phase) {
                if (this.allMandatoryCompleted && this.hasPendingOptionals && this.optionalTasks.length > 0) {
                    this.activeTab = 'optional';
                }
                // En cualquier otro caso conserva 'mandatory' (default). Evita que la pestaña salte sola por socket.
                this.hasInitializedTab = true;
            }

            this.isLoading = false;
        });
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    get tasks(): OnboardingTask[] {
        return this.phase?.tasks || [];
    }

    get mandatoryTasks(): OnboardingTask[] {
        return this.onboardingService.getMandatoryTasks(this.phase);
    }

    get optionalTasks(): OnboardingTask[] {
        return this.onboardingService.getOptionalTasks(this.phase);
    }

    /**
     * Todas las obligatorias de la fase están completas. `every` sobre [] devuelve `true` (fases sin
     * obligatorias, ej. Fase 3), pero la inicialización de pestaña además exige opcionales pendientes.
     */
    get allMandatoryCompleted(): boolean {
        return this.mandatoryTasks.every(task => task.isCompleted);
    }

    /** Hay al menos una tarea opcional pendiente (sin completar). */
    get hasPendingOptionals(): boolean {
        return this.optionalTasks.some(task => !task.isCompleted);
    }

    /**
     * Las pestañas Obligatorias/Opcionales solo tienen sentido si la fase tiene AMBOS tipos de tareas.
     * Fase 1 (solo obligatorias) y Fase 3 (solo opcionales) → se ocultan (evita el confuso "Obligatorias 0/0").
     */
    get showTabs(): boolean {
        return this.mandatoryTasks.length > 0 && this.optionalTasks.length > 0;
    }

    get visibleTasks(): OnboardingTask[] {
        // Sin pestañas (la fase no tiene AMBOS tipos) mostramos TODAS las tareas de la fase: en Fase 1 son
        // todas obligatorias y en Fase 3 todas opcionales, así la lista siempre se renderiza aunque `activeTab`
        // siga en 'mandatory' por defecto. Con ambas pestañas (Fase 2) respetamos la pestaña activa.
        if (!this.showTabs) return this.tasks;
        return this.activeTab === 'mandatory' ? this.mandatoryTasks : this.optionalTasks;
    }

    get completedTasks(): number {
        return this.onboardingService.getCompletedCount(this.tasks);
    }

    get completionPercent(): number {
        return this.onboardingService.getCompletionPercent(this.tasks);
    }

    get mandatoryCompleted(): number {
        return this.onboardingService.getCompletedCount(this.mandatoryTasks);
    }

    get optionalCompleted(): number {
        return this.onboardingService.getCompletedCount(this.optionalTasks);
    }

    setTab(tab: OnboardingTab): void {
        if (tab === 'optional' && !this.optionalTasks.length) return;
        this.activeTab = tab;
    }

    startTask(task: OnboardingTask): void {
        if (!task.link) return;
        // Mismo comportamiento que la TaskCard del dashboard: analytics + auto-trigger del tour tolerante a
        // navegación. `requestTourForTask` encola el tour en `_pending` del orquestador; el tour se dispara en
        // el NavigationEnd correcto (con el gate del modal), NO con un setTimeout ciego → sin el race de
        // "blackout en la página equivocada". Solo se encola si la tarea está PENDIENTE (el botón "Empezar"
        // ya solo aparece con !isCompleted, pero lo blindamos igual; el orquestador lo re-verifica).
        this.analytics.stepClicked(task);
        if (!task.isCompleted) {
            this.tourOrchestrator.requestTourForTask(task.task_id, task.link);
        }
        this.activeModal.close();
        this.router.navigateByUrl(task.link);
    }
}
